/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
// 授信流程
import { session } from '@/shared/utils/storage';
import { ubtButton } from '@/shared/utils/ubt';
import VueRouter, { RouteConfig } from 'vue-router';
import { NewVersionEnum } from '@/enums/newVersionRoute';
import {
  verifySceneMapping,
  getVerifyCreditPageTitle,
  getVerifyCreditPageNavBarCustomProps,
} from '@/shared/verify-credit-controller';
import { VerifyTypeEnum } from '@/api/verify-credit/types';
import { preloadVerifyCreditIdentity } from '@/shared/tools/ocr/utils/loader';
import { VerifySceneEnum } from '@/shared/verify-credit-controller/types';
import { customSceneUIControls } from '@/views_v2/verify-credit/const';
import { VerifyCreditRouterMeta } from '@/views_v2/verify-credit/types';
import { dcCreditSceneEnum } from '@/api/preApplyChoose/types';
import { RouterV2Enum } from '../router-names';
import { tryBackPathOrHome } from '../utils';

export default (router: VueRouter): RouteConfig[] => ([
  {
    /** 此页面已下线，请使用RouterV2Enum.verifyCredi替代 */
    path: RouterV2Enum.baseInfo,
    name: 'BaseInfoDeprecated',
    props: (route) => ({
      pageTitle: '快速申请',
      type: String(route.query.type),
      defaultStep: Number(route.query.step),
    }),
    beforeEnter: (to, _, next) => {
      const { type, step } = to.query || {};

      next({
        path: RouterV2Enum.verifyCredit,
        query: {
          /**
           * 身份证清晰分不足 or 身份证过期场景，使用特殊场景：CREDIT_WITH_INVALID_IDENTITY
           * 其他场景，统一使用授信资料填写流程
           */
          scene: type === 'modify' && Number(step) === 2
            ? VerifySceneEnum.CREDIT_WITH_INVALID_IDENTITY
            : VerifySceneEnum.CREDIT_PRE_VERIFY,
        },
      });
    },
  },
  {
    path: RouterV2Enum.verifyCredit,
    name: 'VerifyCredit',
    component: () => import(/* webpackChunkName: "credit" */ '@/views_v2/verify-credit'),
    props: (route) => ({
      pageTitle: '快速申请',
      jumpId: route.query.jumpId,
      scene: route.query.scene,
      openMode: route.query.openMode,
      commodityIds: route.query.commodityIds || '',
      creditId: route.query.creditId || '',
      currentStage: route.query.currentStage || '',
    }),
    beforeEnter: (to, _, next) => {
      const { scene } = to.query || {};
      const verifyType = verifySceneMapping(scene);
      const setMeta = (to.meta) as VerifyCreditRouterMeta;

      if (scene) {
        setMeta.title = getVerifyCreditPageTitle(verifyType as VerifyTypeEnum);
        setMeta.navBarCustomProps = getVerifyCreditPageNavBarCustomProps(
          setMeta.navBarCustomProps,
          verifyType as VerifyTypeEnum,
        );
        setMeta.isShowH5Nav = customSceneUIControls.getIsUseImmerseNarbar(scene as VerifySceneEnum);
      }

      preloadVerifyCreditIdentity();

      next();
    },
    meta: {
      pageId: 'finance_personalInformation',
      title: '',
      isShowH5Nav: true,
      isControlCoffeeH5Nav: true,
      backConfirmDialog: {
        title: '确定要退出?',
        message: '只差一步，你就可以申请贷款哦',
        confirmButtonText: '继续认证',
        cancelButtonText: '仍要退出',
        beforeConfirm: () => ubtButton('finance_personalInformation', '臻有钱_快速认证_退出继续认证'),
      },
    },
  },

  {
    path: RouterV2Enum.cashActivityDisqualification,
    name: 'CashActivityDisqualification',
    component: () => import(/* webpackChunkName: "credit-cash-activity-disqualification" */ '@/views_v2/cash-activity-result/cash-activity-disqualification'),
    meta: {
      pageId: 'finance_loan_ocr_identify_activity_disqualification',
      title: '结果页',
      isShowH5Nav: true,
      /** isShowCoffeeH5Nav配置为true是，isShowH5Nav也要配置为true */
      isShowCoffeeH5Nav: true,
      navbar: {
        titleIsCenter: true,
      },
    },
  },

  {
    path: RouterV2Enum.cashActivitySuccess,
    name: 'CashActivitySuccess',
    component: () => import(/* webpackChunkName: "credit-cash-activity-success" */ '@/views_v2/cash-activity-result/cash-activity-success'),
    meta: {
      pageId: 'finance_loan_ocr_identify_activity_success',
      title: '结果页',
      isShowH5Nav: true,
      /** isShowCoffeeH5Nav配置为true是，isShowH5Nav也要配置为true */
      isShowCoffeeH5Nav: true,
      navbar: {
        titleIsCenter: true,
      },
    },
  },

  {
    path: RouterV2Enum.preApplyChoose,
    name: 'pre-apply-choose',
    component: () => import(/* webpackChunkName: "credit-pre-apply-choose" */ '@/views_v2/pre-apply-choose'),
    meta: {
      pageId: '臻有钱_申请额度页',
      upgradePageId: 'finance_upgradeAAppling',
      title: '加载中', // 标题动态加载的，先显示加载中
      desc: '申请额度',
    },
  },
  /**
   * 此路由与RouterV2Enum.preApplyChoose使用同一页面，不同点在于：
   * RouterV2Enum.preApplyChoose按正常线上流程中，先进行正常流量分发，没有分发到渠道才走贷超半流程分发流程
   * RouterV2Enum.dcPreApplyChoose无需走前置的流量分发，直接走贷超半流程分发流程
   * 需求地址：http://wiki.hellobike.com/pages/viewpage.action?pageId=392767111
   */
  {
    path: RouterV2Enum.dcPreApplyChoose,
    name: 'dc-pre-apply-choose',
    component: () => import(/* webpackChunkName: "credit-pre-apply-choose" */ '@/views_v2/pre-apply-choose'),
    props: (route) => ({
      /** 是否贷超半流程授信 */
      isDcPreApplyChoose: true,
      /** 来源 */
      from: String(route.query.from || ''),
      /** 是否纯贷超半流程场景，不展示分期商城、超会、车抵贷逻辑 */
      isOnlyDcHalf: route.query.isOnlyDcHalf === 'true',
    }),
    meta: {
      pageId: 'finance_loan_dcApplyChoose',
      title: '加载中', // 标题动态加载的，先显示加载中
      desc: '贷超半流程申请额度',
    },
  },
  /**
   * 分期商城授信协议专用地址，不和授信逻辑、贷超半流程等逻辑耦合
   * 目前流程主要用于首页跳转过来
   */
  {
    path: RouterV2Enum.fqMallPreApply,
    name: 'installment-mall-pre-apply',
    component: () => import(/* webpackChunkName: "fq-pre-apply-apply" */ '@/views_v2/installment-mall/pre-apply/index.vue'),
    props: (route) => ({
      /** 来源 */
      from: String(route.query.from || ''),
      /** 分期商城待授信的商品ID */
      productId: String(route.query.productId || ''),
    }),
    meta: {
      pageId: 'finance_loan_installmentMallApplyChoose',
      title: '', // 标题动态加载的，先显示加载中
      desc: '分期商城申请额度',
      isShowH5Nav: true,
      isShowCoffeeH5Nav: true,
    },
  },
  {
    path: RouterV2Enum.middleIncomeContract,
    name: 'middle-income-contract',
    component: () => import(/* webpackChunkName: "credit-middle-income-contract" */ '@/views_v2/middle-income-contract/index.vue'),
    props: (route) => {
      // 解析分期商城产品ID列表
      let shoppingMallCommoditys: string[] = [];
      const shoppingMallCommoditysStr = route.query.shoppingMallCommoditys;
      if (shoppingMallCommoditysStr) {
        try {
          shoppingMallCommoditys = JSON.parse(shoppingMallCommoditysStr);
        } catch (error) {
          shoppingMallCommoditys = [];
        }
      }

      // 解析贷超产品ID列表
      let overLoanCommoditys: string[] = [];
      const overLoanCommoditysStr = route.query.overLoanCommoditys;
      if (overLoanCommoditysStr) {
        try {
          overLoanCommoditys = JSON.parse(overLoanCommoditysStr);
        } catch (error) {
          overLoanCommoditys = [];
        }
      }

      return {
        /** 分期商城产品ID列表 */
        shoppingMallCommoditys,
        /** 贷超产品ID列表 */
        overLoanCommoditys,
        /** 业务场景 */
        scene: (route.query.scene as dcCreditSceneEnum) || dcCreditSceneEnum.NO_CHANNEL_AVAILABLE,
        /** 投放来源 */
        from: String(route.query.from || ''),
        /** 申请模式 */
        applyMode: String(route.query.applyMode || ''),
      };
    },
    meta: {
      pageId: 'finance_loan_middleincomeContract',
      title: ' ',
      isShowH5Nav: true,
      isShowCoffeeH5Nav: true,
    },
  },
  {
    // TODO: 此页面已废弃，重定向至新项目授信结果页
    path: RouterV2Enum.preApplyResult,
    name: 'pre-apply-result',
    component: () => import(/* webpackChunkName: "credit-pre-apply-result" */ '@/views_v2/pre-apply-result'),
    props: (route) => ({
      /** app内授信 任务 id */
      taskId: String(route.query.taskId || ''),
      /** 外采授信订单 id */
      sourceOrderId: String(route.query.sourceOrderId || ''),
    }),
    meta: {
      newRedirect: NewVersionEnum.CREDIT_RESULT,
      newRedirectReplace: true,
      pageId: '臻有钱_资料审核结果页',
      title: '审核结果',
      desc: '资料交成功页',
      isShowH5Nav: true,
      // backPath: () => tryBackPathOrHome(RouterV2Enum.myProfile),
      backConfirmDialog: {
        title: '正在为您加急审核',
        message: '退出后仍为您继续申请，请注意查看额度',
        cancelButtonText: '仍要退出',
        confirmButtonText: '继续等待',
        beforeConfirm: () => {
          ubtButton('臻有钱_资料审核结果页', '臻有钱_授信审核中_继续等待');
          ubtButton(
            '臻有钱_资料审核结果页',
            '臻有钱_资料提交成功_退出我再等等',
          );
        },
        beforeCancel: () => {
          ubtButton('臻有钱_资料审核结果页', '臻有钱_授信审核中_仍要退出');
          ubtButton(
            '臻有钱_资料审核结果页',
            '臻有钱_资料提交成功_退出回到首页',
          );

          // 我的去提现
          const myProfileProduct = session.get('myProfile') || '';
          if (myProfileProduct) {
            session.remove('myProfile');
            router.replace(RouterV2Enum.myProfile);
          }
        },
      },
    },
  },

  {
    // TODO: 此页面只从授信结果页跳转进来，授信结果页已重定向到新项目。故此页面已废弃
    path: RouterV2Enum.preApplyFailed,
    name: 'pre-apply-failed',
    component: () => import(
      /* webpackChunkName: "credit-pre-apply-failed" */ '@/views_v2/pre-apply-result/components/pre-apply-failed'
    ),
    meta: {
      newRedirect: NewVersionEnum.CREDIT_RESULT,
      newRedirectReplace: true,
      pageId: '臻有钱_前筛资料审核不通过',
      title: '审核结果',
      desc: '前筛资料审核不通过',
      isShowH5Nav: true,
      backPath: () => tryBackPathOrHome(RouterV2Enum.myProfile),
    },
  },
  {
    path: RouterV2Enum.myPreApply,
    name: 'myPreApply',
    component: () => import(/* webpackChunkName: "credit-my-pre-apply" */ '@/views_v2/my-pre-apply'),
    meta: {
      pageId: '臻有钱_我的额度审批页',
      title: '我的额度审批',
      backPath: RouterV2Enum.myProfile,
      isShowH5Nav: true,
      /** isShowCoffeeH5Nav配置为true是，isShowH5Nav也要配置为true */
      isShowCoffeeH5Nav: true,
    },
  },
  {
    path: RouterV2Enum.aliCreditBlocked,
    name: 'aliCreditBlocked',
    component: () => import(/* webpackChunkName: "credit-alipay-blocked" */ '@/views_v2/alipay-credit-blocked'),
    meta: {
      pageId: 'finance_aliCreditBlocked',
      title: '资料填写成功',
      backPath: RouterV2Enum.aliminiHome,
    },
  },
  {
    path: RouterV2Enum.unitWaitingPage,
    name: 'UnitWaitingPage',
    component: () => import(/* webpackChunkName: "unit-waiting-page" */ '@/views_v2/unit-waiting-page/index.vue'),
    props: (route) => {
      const dcIdListStr = String(route.query.dcIdList || '');
      const dcIdListArray = dcIdListStr ? dcIdListStr.split(',') : [];

      return {
        /** 授信单号 */
        creditTaskId: String(route.query.creditTaskId || ''),
        /** 是否检查贷超半流程协议 */
        isCheckDcContracts: route.query.isCheckDcContracts === 'true',
        /** 贷超渠道ID列表 */
        dcIdList: dcIdListArray,
        /** 贷超场景 */
        dcCreditScene: String(route.query.dcCreditScene || ''),
        /** 授信申请模式 */
        applyMode: String(route.query.applyMode || ''),
        /** 来源 */
        from: String(route.query.from || ''),
        /** 是否二次进入分期商城协议页 */
        source: String(route.query.source || ''),
      };
    },
    meta: {
      pageId: 'finance_loan_shopMall_agreement_check',
      title: '',
      isShowH5Nav: true,
      isShowCoffeeH5Nav: true,
    },
  },
  {
    path: RouterV2Enum.fqMallPreApplySuccess,
    name: 'FqMallPreApplySuccess',
    component: () => import(/* webpackChunkName: "fq-mall-pre-apply-success" */ '@/views_v2/fq-mall-pre-apply-success/index.vue'),
    props: (route) => ({
      creditTaskId: String(route.query.creditTaskId || ''),
      /** 投放来源 */
      from: String(route.query.from || ''),
      /** 是否二次进入分期商城协议页 */
      source: String(route.query.source || ''),
      /** 是否跳过倒计时，1跳过，0不跳过 */
      skipCountdown: String(route.query.skipCountdown || '0'),
    }),
    meta: {
      pageId: 'finance_loan_shopMall_agreement_result',
      title: '申请结果',
      isShowH5Nav: true,
      // isShowCoffeeH5Nav: true,
      isControlCoffeeH5Nav: true,
    },
  },
]);
